import { useEffect, useState } from 'react';

import workerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url';
import { Document, pdfjs } from 'react-pdf';
import { useParams } from 'react-router-dom';

import { RendererBaseProps } from '..';
import { DocViewerLoader } from './doc-viewer-loader';
import { PDFPage } from './pdf-page';
import { useGetDocPDFPresignedURLV2 } from '@/api/queries/useGetDocPDFPresignedURLV2';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

type PDFDocumentRendererProps = RendererBaseProps & {
  // When provided, the renderer uses this URL directly and skips the
  // matter/docId presigned-URL fetch. Used by viewers (e.g. Kim thread files)
  // whose source isn't a regular DocV2.
  fileUrl?: string;
  onDocumentLoadSuccess?: (numPages: number) => void;
  onPageLoadSuccess?: (pageNumber: number, dimensions: { width: number; height: number }) => void;
};

export const PDFDocumentRenderer = ({
  docId,
  fileUrl: providedFileUrl,
  highlights,
  getPageDimensions,
  visiblePages,
  getPagePosition,
  highlightedFactId,
  onHighlightClick,
  highlightedChunkId,
  totalHeight,
  maxPageWidth: providedMaxPageWidth,
  onDocumentLoadSuccess: providedOnDocumentLoadSuccess,
  onPageLoadSuccess,
}: PDFDocumentRendererProps) => {
  const { matterId } = useParams();

  const [documentReady, setDocumentReady] = useState(false);

  const { data: fetchedFileUrl } = useGetDocPDFPresignedURLV2(matterId, providedFileUrl ? undefined : docId);
  const fileUrl = providedFileUrl ?? fetchedFileUrl;

  useEffect(() => {
    setDocumentReady(false);
  }, [fileUrl]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setDocumentReady(true);
    providedOnDocumentLoadSuccess?.(numPages);
  };

  return (
    <div
      className="relative bg-gray-100"
      style={{ height: totalHeight, width: (providedMaxPageWidth ?? 0) + 40, minWidth: '100%' }}
    >
      {fileUrl && (
        <Document
          key={fileUrl}
          loading={
            <div className="relative flex justify-center">
              <div className="fixed my-20 w-full">
                <DocViewerLoader />
              </div>
            </div>
          }
          file={fileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
        >
          {documentReady &&
            visiblePages.map((pageNumber) => {
              const pageDims = getPageDimensions(pageNumber);

              return (
                <PDFPage
                  key={`page-${pageNumber}`}
                  pageNumber={pageNumber}
                  top={getPagePosition(pageNumber)}
                  dimensions={pageDims}
                  highlights={highlights}
                  highlightedFactId={highlightedFactId}
                  onHighlightClick={onHighlightClick}
                  highlightedChunkId={highlightedChunkId}
                  onPageLoadSuccess={onPageLoadSuccess}
                />
              );
            })}
        </Document>
      )}
    </div>
  );
};
